import { NGXLogger } from 'ngx-logger';
import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  UrlTree,
} from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, from, map, of, switchMap } from 'rxjs';
import { AppConstants } from '../commons/app-constants';
import { NetworkStatusService } from '../services/network-status.service';
import { OfflineLeaseService } from '../services/offline-lease.service';
import { PermissionService } from '../services/permission.service';

// Priority-ordered list: first permission the user holds determines the landing route.
const ROUTE_PRIORITY: Array<[string, string]> = [
  ['DASHBOARD_ACCESS', '/dashboard-new'],
  ['TALLY_ACCESS',     '/new-tallysheet'],
  ['SHIPMENT_ACCESS',  '/consignments'],
  ['BP_ACCESS',        '/business-partners'],
  ['REPORT_ACCESS',    '/reports'],
  ['STORAGE_ACCESS',   '/storage'],
  ['EXTRAS_ACCESS',    '/company'],
];

@Injectable({
  providedIn: 'root',
})
export class LoginRedirectGuard implements CanActivate {
  private readonly logger = inject(NGXLogger);

  constructor(
    private http: HttpClient,
    private router: Router,
    private networkStatusService: NetworkStatusService,
    private offlineLeaseService: OfflineLeaseService,
    private permissionService: PermissionService,
  ) {}

  // Shared by the online success branch and the offline-lease-valid branch, so a cold offline
  // launch lands on the same permission-appropriate route a live login would — not a route this
  // user might not even have DASHBOARD_ACCESS for (see the offline branch below for why this
  // used to be a hard-coded '/dashboard-new').
  private pickLandingRoute(perms: Set<string>): UrlTree {
    for (const [perm, dest] of ROUTE_PRIORITY) {
      if (perms.has(perm)) {
        this.logger.debug('[LoginRedirectGuard] Logged in — redirecting to', dest);
        return this.router.createUrlTree([dest]);
      }
    }
    this.logger.debug('[LoginRedirectGuard] Logged in — no specific permission, redirecting to dashboard');
    return this.router.createUrlTree(['/dashboard-new']);
  }

  /**
   * A remembered page is resumed only when it is a real page of the app: it starts with "/" and its first part is a route that exists.
   * A damaged or stale value (for example a stray "/com") is thrown away and the normal landing page is used instead.
   */
  private usableRememberedRoute(value: string | null): string | null {
    if (!value || !value.startsWith('/') || value.startsWith('//')) {
      if (value) localStorage.removeItem('lastActiveRoute');
      return null;
    }
    try {
      const first = this.router.parseUrl(value).root.children['primary']?.segments[0]?.path;
      const known = !!first && this.router.config.some((r) => r.path === first || (r.path ?? '').startsWith(first + '/'));
      if (known) return value;
    } catch { /* not a valid address */ }
    localStorage.removeItem('lastActiveRoute');
    return null;
  }

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const hasForcedLoginFlow =
      route.queryParamMap.has('forceLogin') ||
      route.queryParamMap.has('sessionExpired') ||
      route.queryParamMap.has('error') ||
      route.queryParamMap.has('authError') ||
      route.queryParamMap.has('token') ||
      route.queryParamMap.has('code') ||
      route.queryParamMap.has('forbiddenAccess') ||
      route.queryParamMap.has('inviteComplete') ||
      route.queryParamMap.has('reason') ||
      route.queryParamMap.get('register') === 'true';

    if (hasForcedLoginFlow) {
      this.logger.debug('[LoginRedirectGuard] Forced login flow — skipping session check');
      return of(true);
    }

    // Use a real JWT-protected endpoint to determine login state.
    // The isUserLoggedIn() endpoint lives under /api/v1/login which is in the JWT
    // skip list, so userPrincipal is always null there — it always returns false.
    // Calling /api/v1/users/me/permissions instead:
    //   200  → cookie is valid → user is logged in → redirect to first accessible route
    //   401  → not authenticated → interceptor skips session-expired dialog
    //          (it checks router.url.includes('/login') before showing it) → of(true)
    this.logger.debug('[LoginRedirectGuard] Probing session via permissions endpoint');
    return this.http.get<{ permissions: string[] }>(
      `${AppConstants.API_URL}api/v1/users/me/permissions`
    ).pipe(
      map((response) => {
        // Deep link / share link opened while already logged in — resume the originally
        // requested route instead of the permission-priority landing page.
        const postLoginReturnUrl = localStorage.getItem('postLoginReturnUrl');
        if (postLoginReturnUrl) {
          localStorage.removeItem('postLoginReturnUrl');
          this.logger.debug('[LoginRedirectGuard] Logged in — resuming postLoginReturnUrl', postLoginReturnUrl);
          return this.router.parseUrl(postLoginReturnUrl) as boolean | UrlTree;
        }
        // Android: the app can get rebooted mid-session (OS process kill on rotation/
        // backgrounding — see AppComponent.persistLastActiveRoute), which always reloads
        // at "/" regardless of where the user actually was. Resume there instead of the
        // default landing page. Deliberately a separate key from postLoginReturnUrl (a
        // one-time intentional share-link redirect) — this one is continuously updated
        // and never explicitly consumed/cleared, so it stays accurate for the next reboot.
        const lastActiveRoute = this.usableRememberedRoute(localStorage.getItem('lastActiveRoute'));
        if (lastActiveRoute) {
          this.logger.debug('[LoginRedirectGuard] Logged in — resuming lastActiveRoute', lastActiveRoute);
          return this.router.parseUrl(lastActiveRoute) as boolean | UrlTree;
        }
        const perms = new Set<string>(response?.permissions ?? []);
        return this.pickLandingRoute(perms) as boolean | UrlTree;
      }),
      catchError((error) => {
        // This probe fails not just on a real 401 but also on a plain network error — on a
        // cold app start while offline, this used to always fall through to "show login",
        // permanently stranding an offline user who has a perfectly valid session (this is
        // the guard responsible for bouncing an already-logged-in user away from /login;
        // see also AuthSessionGuard, which had the identical gap for in-app navigation).
        // .error() deliberately — environment.prod.ts sets logLevel:"ERROR", and this app is
        // built with --configuration production for device testing, so .debug() is silently
        // dropped and invisible in logcat.
        const isNetworkFailure = !this.networkStatusService.isOnline || error?.status === 0 || error?.status === undefined;
        // JSON.stringify the payload — Android WebView's console-to-Logcat bridge just
        // stringifies object args as "[object Object]" instead of serializing them.
        this.logger.error('[LoginRedirectGuard] permissions probe failed', JSON.stringify({
          isOnline: this.networkStatusService.isOnline,
          errorStatus: error?.status,
          errorMessage: error?.message,
          isNetworkFailure,
        }));
        if (isNetworkFailure) {
          return from(this.offlineLeaseService.check()).pipe(
            switchMap((result) => {
              this.logger.error('[LoginRedirectGuard] offline lease check result', JSON.stringify(result));
              if (!result.valid || !result.withinGrace) {
                return of(true);
              }
              // Same lastActiveRoute-first resumption as the online branch above (cold Android
              // reboot always reloads at '/' regardless of where the user actually was).
              const lastActiveRoute = this.usableRememberedRoute(localStorage.getItem('lastActiveRoute'));
              if (lastActiveRoute) {
                this.logger.debug('[LoginRedirectGuard] Offline — resuming lastActiveRoute', lastActiveRoute);
                return of(this.router.parseUrl(lastActiveRoute) as boolean | UrlTree);
              }
              // No remembered route — fall back to the same permission-priority pick the online
              // branch uses, off the cached snapshot (syncSessionForGuard's own fetch will fail
              // offline exactly like the probe above just did, landing it in its own Dexie-backed
              // fallback). Previously this just hard-coded '/dashboard-new', which 403'd straight
              // into PermissionGuard for any cached user without DASHBOARD_ACCESS (e.g. a
              // Shipments/BP-only role) — dumping them on /unauthorized on every cold offline
              // launch even though their real cached home route would have worked fine.
              return this.permissionService.syncSessionForGuard().pipe(
                map(() => this.pickLandingRoute(this.permissionService.getSnapshot()) as boolean | UrlTree),
                catchError(() => of(this.router.createUrlTree(['/dashboard-new']) as boolean | UrlTree)),
              );
            }),
            catchError((leaseError) => {
              this.logger.error('[LoginRedirectGuard] offline lease check threw', String(leaseError));
              return of(true);
            }),
          );
        }
        this.logger.debug('[LoginRedirectGuard] Not authenticated — showing login page', {
          status: error?.status,
        });
        return of(true);
      })
    );
  }
}
